import { useI18n } from "@expertloop/i18n/react";
import type { MessageKey } from "@expertloop/i18n";
import { adminApi, AdminApiError } from "../../services/api";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useConfirmedEmailAction } from "./useConfirmedEmailAction";

export interface UserAccessSubject {
	id: string;
	email: string;
	authProvider: string;
	isPasswordless?: boolean;
	emailVerified: boolean;
}

// Codes posés par l'API sur les refus que l'interface doit expliquer
const ERROR_KEYS: Record<string, MessageKey> = {
	user_not_email_account: "adminUserDetail.passwordResetUnavailableGoogle",
	user_passwordless: "adminUserDetail.passwordResetUnavailablePasskey",
	already_verified: "adminUserDetail.alreadyVerified",
	resend_limited: "adminUserDetail.resendLimited",
};

/**
 * Carte « Accès au compte » de la fiche d'un usager : renvoyer un lien de
 * réinitialisation du mot de passe, ou l'email de vérification de l'adresse.
 * Les liens partent par email vers l'usager : l'administrateur ne les voit pas.
 */
export function UserAccessCard({ user }: { user: UserAccessSubject }) {
	const { t } = useI18n();
	const passwordReset = useConfirmedEmailAction();
	const verification = useConfirmedEmailAction();

	const usesPassword = () => user.authProvider === "email" && !user.isPasswordless;
	const canResendVerification = () => user.authProvider === "email" && !user.emailVerified;

	const signInMethodLabel = (): string => {
		if (user.authProvider === "google") return t("adminUserDetail.methodGoogle");
		if (user.isPasswordless) return t("adminUserDetail.methodPasskey");
		return t("adminUserDetail.methodEmail");
	};

	const passwordResetUnavailableReason = (): string | null => {
		if (usesPassword()) return null;
		return user.authProvider === "google"
			? t("adminUserDetail.passwordResetUnavailableGoogle")
			: t("adminUserDetail.passwordResetUnavailablePasskey");
	};

	const describeError = (error: unknown): string => {
		const key = error instanceof AdminApiError && error.code ? ERROR_KEYS[error.code] : undefined;
		return key ? t(key) : t("adminUserDetail.actionFailed");
	};

	const sendPasswordReset = () =>
		passwordReset.confirm(async () => {
			await adminApi.post(`/admin/users/${user.id}/password-reset`);
			return t("adminUserDetail.passwordResetSent", { email: user.email });
		}, describeError);

	const resendVerification = () =>
		verification.confirm(async () => {
			await adminApi.post(`/admin/users/${user.id}/resend-verification`);
			return t("adminUserDetail.verificationSent", { email: user.email });
		}, describeError);

	const unavailableReason = passwordResetUnavailableReason();

	return (
		<div className="info-card">
			<h3>{t("adminUserDetail.accessTitle")}</h3>
			<div className="info-row">
				<span className="info-label">{t("adminUserDetail.signInMethod")}</span>
				<span className="info-value">{signInMethodLabel()}</span>
			</div>

			<div className="access-actions">
				<button
					type="button"
					className="btn btn-outline"
					onClick={passwordReset.ask}
					disabled={!usesPassword() || passwordReset.isSending}
				>
					{t("adminUserDetail.sendPasswordReset")}
				</button>
				{unavailableReason && <p className="access-actions__hint">{unavailableReason}</p>}
				{passwordReset.notice && <Notice notice={passwordReset.notice} />}

				{canResendVerification() && (
					<>
						<button
							type="button"
							className="btn btn-outline"
							onClick={verification.ask}
							disabled={verification.isSending}
						>
							{t("adminUserDetail.resendVerification")}
						</button>
						{verification.notice && <Notice notice={verification.notice} />}
					</>
				)}
			</div>

			{passwordReset.isConfirming && (
				<ConfirmDialog
					title={t("adminUserDetail.sendPasswordResetTitle")}
					message={t("adminUserDetail.sendPasswordResetBody", { email: user.email })}
					confirmLabel={t("adminUserDetail.send")}
					cancelLabel={t("common.cancel")}
					onConfirm={sendPasswordReset}
					onCancel={passwordReset.cancel}
				/>
			)}
			{verification.isConfirming && (
				<ConfirmDialog
					title={t("adminUserDetail.resendVerificationTitle")}
					message={t("adminUserDetail.resendVerificationBody", { email: user.email })}
					confirmLabel={t("adminUserDetail.send")}
					cancelLabel={t("common.cancel")}
					onConfirm={resendVerification}
					onCancel={verification.cancel}
				/>
			)}
		</div>
	);
}

function Notice({ notice }: { notice: { kind: "success" | "error"; text: string } }) {
	return (
		<div className={notice.kind === "success" ? "notice-success" : "error-message"} role="status">
			{notice.text}
		</div>
	);
}
